import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { requestCustomerOtp, verifyCustomerOtp } from "../../legacy/api/customer";
import useCustomerAuth from "../../legacy/hooks/useCustomerAuth";
import useOtpResendCooldown from "../../legacy/hooks/useOtpResendCooldown";
import MobileLayout from "../layouts/MobileLayout";

const digits = (value, max) => String(value || "").replace(/\D/g, "").slice(0, max);

export default function MobileLogin({ initialSignup = false }) {
  const auth = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const redirectTo = next?.startsWith("/") ? next : "/account";
  const [method, setMethod] = useState("phone");
  const [signup, setSignup] = useState(initialSignup || searchParams.get("signup") === "1");
  const [otpSent, setOtpSent] = useState(false);
  const [form, setForm] = useState({ full_name: "", country_code: "+91", phone: "", email: "", otp: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { resendSeconds, startResendCooldown, resetResendCooldown } = useOtpResendCooldown();
  const otpRefs = useRef([]);

  useEffect(() => {
    if (!auth.loading && auth.customer) navigate(redirectTo, { replace: true });
  }, [auth.customer, auth.loading, navigate, redirectTo]);

  const update = (key, value) => {
    const nextValue = key === "phone" ? digits(value, 10) : key === "otp" ? digits(value, 8) : value;
    setForm((current) => ({ ...current, [key]: nextValue, ...(key !== "otp" ? { otp: "" } : {}) }));
    if (key !== "otp") { setOtpSent(false); resetResendCooldown(); }
    setError(""); setNotice("");
  };

  const payload = () => ({
    identifier_type: method,
    country_code: form.country_code,
    phone: form.phone || undefined,
    email: form.email.trim() || undefined,
    full_name: form.full_name.trim() || undefined,
  });

  const validate = () => {
    if (method === "phone" && !/^\d{10}$/.test(form.phone)) return "Enter a valid 10-digit mobile number.";
    if (method === "email" && !form.email.includes("@")) return "Enter a valid email address.";
    if (signup && !form.full_name.trim()) return "Full name is required.";
    if (signup && !/^\d{10}$/.test(form.phone)) return "Mobile number is required for registration.";
    if (signup && !form.email.includes("@")) return "Email is required for registration.";
    return "";
  };

  const sendOtp = async () => {
    const validation = validate();
    if (validation) { setError(validation); return; }
    setSubmitting(true); setError(""); setNotice("");
    try {
      const response = await requestCustomerOtp(payload());
      setSignup((current) => current || Boolean(response?.requiresSignup));
      setOtpSent(true); startResendCooldown();
      setNotice(response?.message || "OTP sent successfully.");
    } catch (err) { setError(err?.message || "Unable to send OTP."); }
    finally { setSubmitting(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!otpSent) { await sendOtp(); return; }
    if (!form.otp) { setError("Enter the OTP sent to you."); return; }
    setSubmitting(true); setError("");
    try {
      const response = await verifyCustomerOtp({ ...payload(), otp: form.otp });
      await auth.applyAuth(response);
      navigate(redirectTo, { replace: true });
    } catch (err) { setError(err?.message || "OTP verification failed."); }
    finally { setSubmitting(false); }
  };

  const switchMethod = (value) => { setMethod(value); setOtpSent(false); resetResendCooldown(); setError(""); setNotice(""); };
  const setOtpDigit = (index, value) => {
    const digit = digits(value, 1);
    const current = form.otp.padEnd(6, " ").split("");
    current[index] = digit || " ";
    update("otp", current.join("").replace(/\s/g, ""));
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const pasteOtp = (event) => {
    const value = digits(event.clipboardData.getData("text"), 6);
    if (!value) return;
    event.preventDefault();
    update("otp", value);
    otpRefs.current[Math.min(value.length, 6) - 1]?.focus();
  };

  return <MobileLayout showBottomNav={false}>
    <section className="hv-section hv-login-heading"><span className="hv-login-mark"><i className="bi bi-person-check" /></span><h1 className="hv-page-title">{signup ? "Create account" : "Welcome back"}</h1><p className="hv-muted-row">Login or register securely with OTP.</p></section>
    <section className="hv-card hv-login-card">
      <div className="hv-tabs"><button type="button" className={`hv-tab ${method === "phone" ? "active" : ""}`} onClick={() => switchMethod("phone")}>Mobile OTP</button><button type="button" className={`hv-tab ${method === "email" ? "active" : ""}`} onClick={() => switchMethod("email")}>Email OTP</button></div>
      {error ? <p className="hv-auth-message error" role="alert">{error}</p> : null}{notice ? <p className="hv-auth-message success">{notice}</p> : null}
      <form className="hv-search-card" onSubmit={submit}>
        {signup ? <div className="hv-field"><label>Full name *</label><input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Your full name" autoComplete="name" /></div> : null}
        {(method === "phone" || signup) ? <div className="hv-field"><label>Mobile number *</label><div className="hv-phone-control"><input value={form.country_code} readOnly aria-label="Country code"/><input required value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="10-digit mobile number" inputMode="numeric" autoComplete="tel" /></div></div> : null}
        {(method === "email" || signup) ? <div className="hv-field"><label>Email address *</label><input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" autoComplete="email" /></div> : null}
        {otpSent ? <fieldset className="hv-mobile-otp"><legend>Verification code</legend><div onPaste={pasteOtp}>{Array.from({length:6}).map((_,index)=><input key={index} ref={(node)=>{otpRefs.current[index]=node;}} value={form.otp[index]||""} onChange={(e)=>setOtpDigit(index,e.target.value)} onKeyDown={(e)=>{if(e.key==="Backspace"&&!form.otp[index]&&index>0)otpRefs.current[index-1]?.focus();}} inputMode="numeric" autoComplete={index===0?"one-time-code":"off"} aria-label={`OTP digit ${index+1}`} maxLength={1}/>)}</div></fieldset> : null}
        <button className="hv-primary-btn" disabled={submitting} type="submit">{submitting ? "Please wait..." : otpSent ? (signup ? "Verify OTP & Register" : "Verify OTP") : "Send OTP"}</button>
        {otpSent ? <button className="hv-auth-resend" type="button" disabled={submitting || resendSeconds > 0} onClick={sendOtp}>{resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : "Resend OTP"}</button> : null}
      </form>
      <p className="hv-muted-row hv-auth-switch">{signup ? "Already registered?" : "New to Homevilas?"} <button type="button" onClick={() => { setSignup(!signup); setOtpSent(false); resetResendCooldown(); }}>{signup ? "Login" : "Register"}</button></p>
    </section>
    <p className="hv-auth-legal">By continuing, you agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
  </MobileLayout>;
}
